/**
 * OP Readiness — Dispatch monitor tab (Stage 7b, read-only).
 * Browses sync.OPDispatchQueue ⟕ OPDispatchResult: status/mode/dead-letter filters, plus a
 * per-dispatch drill showing the PreRead snapshot / Request / Merged / Response JSON. Shows the
 * dry-run rows today; becomes the live writer monitor once the (gated) writer dispatches.
 * NO controls (enqueue/pause/resume) — those land with the live writer.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchOPReadinessDispatch,
  fetchOPReadinessDispatchDetail,
  isUnavailable,
  type DispatchListRow,
} from '@/lib/op-readiness';
import { StatusBadge, JsonBlock, UnavailableNotice, formatDateTime } from './shared';

const STATUSES = ['', 'PENDING', 'IN_PROGRESS', 'SUCCEEDED', 'FAILED_RETRYABLE', 'DEAD_LETTER', 'SKIPPED'];

function DispatchDetailPanel({ dispatchId, onClose }: { dispatchId: string; onClose: () => void }) {
  const detailQuery = useQuery({
    queryKey: ['op-readiness', 'dispatch', dispatchId],
    queryFn: () => fetchOPReadinessDispatchDetail(dispatchId),
  });
  const data = detailQuery.data && !isUnavailable(detailQuery.data) ? detailQuery.data : null;

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Dispatch {dispatchId.slice(0, 8)} — attempt history</h3>
        <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
      </div>
      {detailQuery.isLoading && <Skeleton className="h-40 w-full rounded-lg" />}
      {data && !data.found && <p className="text-sm text-muted-foreground">Dispatch not found.</p>}
      {data?.found && (
        <div className="space-y-4">
          {(data.results ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">No result rows recorded yet.</p>
          )}
          {(data.results ?? []).map((r) => (
            <div key={r.DispatchResultId} className="rounded-md border p-3">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded bg-muted px-2 py-0.5 font-medium">{r.Mode ?? '—'}</span>
                <span>{r.Success ? '✓ success' : '✗ not-success'}</span>
                {r.HttpStatus != null && <span className="tabular-nums">HTTP {r.HttpStatus}</span>}
                <span className="text-muted-foreground">{formatDateTime(r.CreatedAt)}</span>
                {r.ErrorMessage && <span className="text-red-600">{r.ErrorMessage}</span>}
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                <JsonBlock label="Pre-read snapshot (rollback source)" value={r.preReadSnapshot} />
                <JsonBlock label="Request (what we would send)" value={r.request} />
                <JsonBlock label="Merged (post-reconciliation)" value={r.merged} />
                <JsonBlock label="Response" value={r.response} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DispatchBrowser() {
  const [status, setStatus] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const dispatchQuery = useQuery({
    queryKey: ['op-readiness', 'dispatch-list', status, activeOnly, page],
    queryFn: () =>
      fetchOPReadinessDispatch({ status: status || undefined, activeOnly, page, pageSize: 50 }),
    refetchInterval: 30000,
  });

  if (dispatchQuery.data && isUnavailable(dispatchQuery.data)) {
    return <UnavailableNotice reason={dispatchQuery.data.reason} />;
  }
  if (dispatchQuery.isLoading) return <Skeleton className="h-64 w-full rounded-lg" />;

  const data = dispatchQuery.data && !isUnavailable(dispatchQuery.data) ? dispatchQuery.data : null;
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / 50));

  return (
    <div className="space-y-4">
      {data && (data.statusRollup.length > 0 || data.modeRollup.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {data.statusRollup.map((s) => (
            <div key={s.DispatchStatus} className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
              <StatusBadge status={s.DispatchStatus} />
              <span className="tabular-nums text-muted-foreground">{s.Count}</span>
            </div>
          ))}
          {data.modeRollup.map((m) => (
            <div key={m.ModeRollup} className="flex items-center gap-1.5 rounded-md border border-dashed px-2 py-1 text-xs">
              <span className="font-medium">{m.ModeRollup}</span>
              <span className="tabular-nums text-muted-foreground">{m.Count}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s || 'Any status'}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={activeOnly} onChange={(e) => { setActiveOnly(e.target.checked); setPage(1); }} />
          Active only
        </label>
        <span className="ml-auto text-sm text-muted-foreground">
          {data?.total ?? 0} dispatches · page {page}/{pages}
        </span>
      </div>

      {data && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2">Created</th><th className="px-3 py-2">EAN</th>
                <th className="px-3 py-2">Title</th><th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Mode</th><th className="px-3 py-2">Attempts</th>
                <th className="px-3 py-2">Last error / dead-letter</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r: DispatchListRow) => (
                <tr key={r.DispatchId} className="border-b last:border-0">
                  <td className="whitespace-nowrap px-3 py-2">{formatDateTime(r.CreatedAt)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.EAN ?? '—'}</td>
                  <td className="max-w-[18rem] truncate px-3 py-2">{r.Title ?? '—'}</td>
                  <td className="px-3 py-2"><StatusBadge status={r.DispatchStatus} /></td>
                  <td className="px-3 py-2 text-xs">{r.Mode ?? '—'}</td>
                  <td className="px-3 py-2 tabular-nums">{r.AttemptCount}</td>
                  <td className="max-w-[20rem] truncate px-3 py-2 text-xs text-muted-foreground">
                    {r.DeadLetterReason ?? r.LastError ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    <Button size="sm" variant="outline" onClick={() => setOpenId(r.DispatchId)}>Inspect</Button>
                  </td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-muted-foreground">No dispatches for these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </div>

      {openId && <DispatchDetailPanel dispatchId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
